import { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, FileText, Clock, User, ShoppingCart, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LoadQuotationModal = ({ open, onClose, onLoadQuotation }) => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedQuotation, setSelectedQuotation] = useState(null);

  useEffect(() => {
    if (open) {
      fetchQuotations();
    }
  }, [open]);

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/quotations?status=PENDING`);
      setQuotations(response.data);
    } catch (error) {
      console.error("Error fetching quotations:", error);
      toast.error("Failed to load quotations");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredQuotations = quotations.filter(q => 
    q.quotation_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    q.customer_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleLoadQuotation = () => {
    if (!selectedQuotation) {
      toast.error("Please select a quotation");
      return;
    }

    // Convert quotation items to cart format
    const cartItems = selectedQuotation.items.map(item => ({
      product_id: item.product_id,
      name: item.name,
      quantity: item.quantity,
      unit_price: item.unit_price,
      total: item.total,
      no_discount: item.no_discount || false,
      from_quotation: selectedQuotation.id
    }));

    onLoadQuotation(cartItems, selectedQuotation);
    toast.success(`Loaded ${selectedQuotation.quotation_number} to cart`);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            Load Quotation to Sale
          </DialogTitle>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by quote number or customer name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Quotation List */}
        <div className="flex-1 overflow-y-auto border rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading quotations...</span>
            </div>
          ) : filteredQuotations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No pending quotations found</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredQuotations.map((quote) => (
                <div
                  key={quote.id}
                  onClick={() => setSelectedQuotation(quote)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedQuotation?.id === quote.id ? "bg-blue-50 border-l-4 border-blue-500" : ""
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold text-blue-600">{quote.quotation_number}</div>
                      <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                        <User className="h-3 w-3" />
                        {quote.customer_name || "Walk-in Customer"}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(quote.created_at)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-lg">R {quote.total?.toFixed(2)}</div>
                      <div className="text-xs text-gray-500">{quote.items?.length || 0} items</div>
                      <span className={`text-xs px-2 py-0.5 rounded mt-1 inline-block ${
                        quote.quote_type === "ACCOUNT QUOTE" 
                          ? "bg-blue-100 text-blue-700" 
                          : "bg-gray-100 text-gray-700"
                      }`}>
                        {quote.quote_type}
                      </span>
                    </div>
                  </div>
                  
                  {/* Show items preview when selected */}
                  {selectedQuotation?.id === quote.id && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="text-sm font-medium text-gray-700 mb-2">Items:</div>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {quote.items?.map((item, idx) => (
                          <div key={idx} className="text-sm flex justify-between text-gray-600">
                            <span>{item.quantity}x {item.name}</span>
                            <span>R {item.total?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleLoadQuotation}
            disabled={!selectedQuotation}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            Load to Cart
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoadQuotationModal;
